// Connectivity Monitor Service
// Tracks online/offline status and provides event handling for network changes

class ConnectivityMonitor {
    constructor() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        this.listeners = [];
        this.offlineStartTime = null;
        this.offlineDuration = 0;
        this._tickInterval = null;

        // Restore persisted offline start time if browser reports offline
        try {
            const persisted = localStorage.getItem('offlineStartTime');
            if (!this.isOnline) {
                if (persisted) {
                    const ts = parseInt(persisted, 10);
                    if (!isNaN(ts)) {
                        this.offlineStartTime = ts;
                    }
                }
                // If no persisted time, set now as start
                if (!this.offlineStartTime) {
                    this.offlineStartTime = Date.now();
                    try { localStorage.setItem('offlineStartTime', String(this.offlineStartTime)); } catch (e) {}
                }
            }
        } catch (e) {
            // ignore storage errors
        }
        
        this._initEventListeners();
        if (!this.isOnline) {
            this._startTickTimer();
        }
    }

    _initEventListeners() {
        window.addEventListener('online', () => this._handleOnline());
        window.addEventListener('offline', () => this._handleOffline());
    }

    /**
     * Handle the browser-reported "online" event.
     * Flips `isOnline` to true, finalises the recorded offline duration,
     * persists nothing, and notifies subscribers. Idempotent if already online.
     * @private
     */
    _handleOnline() {
        const wasOffline = !this.isOnline;
        this.isOnline = true;
        
        if (wasOffline && this.offlineStartTime) {
            this.offlineDuration = Date.now() - this.offlineStartTime;
            this.offlineStartTime = null;
        }
        try {
            localStorage.removeItem('offlineStartTime');
        } catch (e) {}
        this._stopTickTimer();
        
        this._notifyListeners('online', {
            wasOffline,
            offlineDuration: this.offlineDuration
        });
    }

    /**
     * Handle the browser-reported "offline" event.
     * Flips `isOnline` to false, records the offline start time (so the
     * 3-hour Sale Vault limit can be enforced later), persists it to
     * localStorage, starts the tick timer, and notifies subscribers.
     * @private
     */
    _handleOffline() {
        this.isOnline = false;
        this.offlineStartTime = Date.now();
        try {
            localStorage.setItem('offlineStartTime', String(this.offlineStartTime));
        } catch (e) {}
        this._startTickTimer();
        
        this._notifyListeners('offline', {
            timestamp: this.offlineStartTime
        });
    }

    _notifyListeners(event, data) {
        this.listeners.forEach(listener => {
            if (listener.event === event || listener.event === 'all') {
                try {
                    listener.callback(data);
                } catch (error) {
                    console.error('Error in connectivity listener:', error);
                }
            }
        });
    }

    _startTickTimer() {
        if (this._tickInterval) return;
        this._tickInterval = setInterval(() => {
            if (!this.isOnline && this.offlineStartTime) {
                // emit tick event once per second while offline
                this._notifyListeners('tick', { now: Date.now() });
            }
        }, 1000);
    }

    _stopTickTimer() {
        if (this._tickInterval) {
            clearInterval(this._tickInterval);
            this._tickInterval = null;
        }
    }

    /**
     * Subscribe to connectivity events
     * @param {String} event - 'online', 'offline', or 'all'
     * @param {Function} callback - Callback function to invoke on event
     * @returns {Function} - Unsubscribe function
     */
    subscribe(event, callback) {
        const listener = { event, callback };
        this.listeners.push(listener);
        
        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * Check current online status
     * @returns {Boolean} - True if online, false otherwise
     */
    getStatus() {
        return this.isOnline;
    }

    /**
     * Explicitly set online status (used by native/platform bridges)
     * @param {boolean} isOnline
     */
    setOnline(isOnline) {
        if (typeof isOnline !== 'boolean' || isOnline === this.isOnline) return;
        if (isOnline) {
            this._handleOnline();
        } else {
            this._handleOffline();
        }
    }

    /**
     * Get offline duration in milliseconds
     * @returns {Number} - Milliseconds offline (0 if online)
     */
    getOfflineDuration() {
        if (this.isOnline) {
            return 0;
        }
        if (this.offlineStartTime) {
            return Date.now() - this.offlineStartTime;
        }
        return 0;
    }

    /**
     * Get offline duration formatted as human-readable string
     * @returns {String} - Formatted duration (e.g., "2h 30m")
     */
    getOfflineDurationFormatted() {
        const duration = this.getOfflineDuration();
        if (duration === 0) {
            return 'Online';
        }
        
        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        
        const remainingMinutes = minutes % 60;
        const remainingSeconds = seconds % 60;
        
        if (hours > 0) {
            return `${hours}h ${remainingMinutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${remainingSeconds}s`;
        } else {
            return `${seconds}s`;
        }
    }

    /**
     * Check if offline duration exceeds threshold
     * @param {Number} thresholdMs - Threshold in milliseconds
     * @returns {Boolean} - True if offline duration exceeds threshold
     */
    isOfflineLongerThan(thresholdMs) {
        return this.getOfflineDuration() > thresholdMs;
    }

    /**
     * Get remaining time before offline limit (3 hours)
     * @param {Number} limitMs - Limit in milliseconds (default 3 hours)
     * @returns {Number} - Remaining milliseconds (0 if online or limit exceeded)
     */
    getRemainingOfflineTime(limitMs = 3 * 60 * 60 * 1000) {
        if (this.isOnline) {
            return limitMs;
        }
        const duration = this.getOfflineDuration();
        const remaining = limitMs - duration;
        return Math.max(0, remaining);
    }

    /**
     * Get remaining time formatted
     * @param {Number} limitMs - Limit in milliseconds (default 3 hours)
     * @returns {String} - Formatted remaining time
     */
    getRemainingOfflineTimeFormatted(limitMs = 3 * 60 * 60 * 1000) {
        const remaining = this.getRemainingOfflineTime(limitMs);
        
        if (remaining === 0) {
            return 'Limit reached';
        }
        
        const seconds = Math.floor(remaining / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        
        const remainingMinutes = minutes % 60;
        
        if (hours > 0) {
            return `${hours}h ${remainingMinutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m`;
        } else {
            return `${seconds}s`;
        }
    }

    /**
     * Test connectivity by attempting a lightweight network request
     * @returns {Promise<Boolean>} - True if request succeeds
     */
    async testConnectivity() {
        // Prefer a real network reachability test to our backend, with graceful fallbacks
        const tryFetch = async (url, options = {}) => {
            const controller = new AbortController();
            const timeout = options.timeout || 4000;
            const id = setTimeout(() => controller.abort(), timeout);
            try {
                const res = await fetch(url, {
                    method: options.method || 'GET',
                    cache: 'no-cache',
                    mode: options.mode || 'no-cors', // no-cors allows opaque pass-through when CORS is strict
                    signal: controller.signal,
                });
                clearTimeout(id);
                // In no-cors mode, response.ok is false for opaque responses; treat any resolution as reachable
                return true;
            } catch (e) {
                clearTimeout(id);
                return false;
            }
        };

        // 1) Try the configured server API base (most representative of actual app connectivity)
        if (typeof window !== 'undefined' && window.serverApi) {
            const ok = await tryFetch(window.serverApi, { timeout: 4000 });
            if (ok) return true;
        }

        // 2) Try a well-known public endpoint (DNS + general internet)
        const publicOk = await tryFetch('https://www.google.com/generate_204', { timeout: 3000 });
        if (publicOk) return true;

        // 3) Fallback to favicon on current origin as last resort
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);
            const response = await fetch('/favicon.ico', { method: 'HEAD', cache: 'no-cache', signal: controller.signal });
            clearTimeout(timeoutId);
            return response.ok;
        } catch (e) {
            // 4) Ultimately fallback to navigator.onLine so we never hard-fail
            return typeof navigator !== 'undefined' ? navigator.onLine : false;
        }
    }

    /**
     * Force check and update online status
     * @returns {Promise<Boolean>} - Updated online status
     */
    async forceCheck() {
        const actuallyOnline = await this.testConnectivity();
        
        if (actuallyOnline !== this.isOnline) {
            if (actuallyOnline) {
                this._handleOnline();
            } else {
                this._handleOffline();
            }
        }
        
        return actuallyOnline;
    }
}

// Create singleton instance
const connectivityMonitor = new ConnectivityMonitor();

export default connectivityMonitor;
